import { useEffect, useState } from "react";
import { X, EnvelopeSimple, Check, Eye, CursorClick, WarningOctagon, ShieldWarning, UserMinus } from "@phosphor-icons/react";
import api from "../../lib/api";

const CHANNEL_LABELS = { email: "Email", whatsapp: "WhatsApp", sms: "SMS", whatsapp_api: "WhatsApp API" };

const STAGES = [
  { key: "inviato_il", label: "Inviato", icon: EnvelopeSimple, cls: "bg-zinc-100 text-zinc-600" },
  { key: "consegnato_il", label: "Consegnato", icon: Check, cls: "bg-emerald-100 text-emerald-700" },
  { key: "aperto_il", label: "Aperto", icon: Eye, cls: "bg-sky-100 text-sky-700" },
  { key: "cliccato_il", label: "Click", icon: CursorClick, cls: "bg-cyan-100 text-cyan-700" },
  { key: "bounce_il", label: "Bounce", icon: WarningOctagon, cls: "bg-amber-100 text-amber-700" },
  { key: "spam_il", label: "Spam", icon: ShieldWarning, cls: "bg-red-100 text-red-700" },
  { key: "disiscritto_il", label: "Disiscritto", icon: UserMinus, cls: "bg-zinc-200 text-zinc-600" },
];

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

export default function CampaignStats({ campaign, onClose }) {
  const [stats, setStats] = useState(null);
  const [recipients, setRecipients] = useState(null);
  const [showRecipients, setShowRecipients] = useState(false);

  useEffect(() => {
    api.get(`/marketing/campaigns/${campaign.id}/stats`).then((r) => setStats(r.data)).catch(() => {});
    api.get(`/marketing/campaigns/${campaign.id}/recipients`).then((r) => setRecipients(r.data)).catch(() => setRecipients([]));
  }, [campaign.id]);

  if (!campaign) return null;
  const ch = campaign.channel;

  const channelMetrics = stats ? (
    ch === "email" ? [
      ["Inviati", stats.sent], ["Consegnati", stats.delivered],
      ["Aperture", stats.opened_total], ["Tasso apertura", `${stats.tasso_apertura}%`],
      ["Click", stats.clicked], ["CTR", `${stats.ctr}%`],
      ["Bounce", stats.bounced], ["Unsubscribe", stats.unsubscribed],
    ] : ch === "sms" ? [
      ["Inviati", stats.sent], ["Consegnati", stats.delivered], ["Falliti", stats.failed],
    ] : [
      ["Inviati", stats.sent], ["Consegnati", stats.delivered],
      ["Letti", stats.read + stats.opened], ["Risposte", stats.replied],
      ["Click", stats.clicked], ["Errori", stats.failed + stats.bounced],
    ]
  ) : [];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-xl sm:rounded-xl rounded-t-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="campaign-stats">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div className="min-w-0">
            <h3 className="font-display font-bold text-lg truncate">{campaign.name}</h3>
            <p className="text-xs text-zinc-500">{CHANNEL_LABELS[ch]} · {campaign.recipients_count || 0} destinatari · costo €{campaign.estimated_cost || 0}</p>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-500" data-testid="stats-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">Performance canale</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {channelMetrics.map(([l, v]) => (
                <div key={l} className="bg-zinc-50 border border-zinc-200 rounded-lg p-3" data-testid={`metric-${l.toLowerCase().replace(/\s/g, "-")}`}>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{l}</div>
                  <div className="font-display text-xl font-black text-zinc-900">{v}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">Metriche Dishenda</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {stats && [
                ["Coupon utilizzati", stats.coupon_utilizzati, "coupon"],
                ["Prenotazioni generate", stats.prenotazioni_generate, "prenotazioni"],
                ["Fatturato attribuito", `€${stats.fatturato_attribuito}`, "fatturato"],
                ["ROI stimato", stats.roi_stimato != null ? `${stats.roi_stimato}x` : "—", "roi"],
              ].map(([l, v, k]) => (
                <div key={l} className="bg-zinc-900 text-white rounded-lg p-3" data-testid={`dmetric-${k}`}>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400">{l}</div>
                  <div className="font-display text-xl font-black">{v}</div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <button onClick={() => setShowRecipients(!showRecipients)} data-testid="recipients-toggle"
              className="w-full flex items-center justify-between text-left bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3 hover:bg-zinc-100 transition-colors">
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-600">
                Dettaglio destinatari {recipients ? `(${recipients.length})` : ""}
              </span>
              <span className="text-zinc-400 text-xs">{showRecipients ? "▲" : "▼"}</span>
            </button>
            {showRecipients && (
              <div className="mt-2 border border-zinc-200 rounded-lg divide-y divide-zinc-100 max-h-72 overflow-y-auto" data-testid="recipients-section">
                {recipients === null && <div className="px-4 py-6 text-center text-xs text-zinc-400">Caricamento...</div>}
                {recipients && recipients.length === 0 && (
                  <div className="px-4 py-6 text-center text-xs text-zinc-400">Nessun destinatario per questa campagna.</div>
                )}
                {recipients && recipients.map((r) => (
                  <div key={r.customer_id} className="px-4 py-3" data-testid={`recipient-row-${r.customer_id}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-sm font-semibold text-zinc-900 truncate">{r.nome}</div>
                      <div className="text-[11px] text-zinc-400 truncate">{r.email || r.telefono || ""}</div>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {STAGES.filter((s) => r[s.key]).map((s) => (
                        <span key={s.key} title={`${s.label} il ${fmtTime(r[s.key])}`}
                          data-testid={`badge-${s.key}-${r.customer_id}`}
                          className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${s.cls}`}>
                          <s.icon size={11} weight="bold" /> {s.label} · {fmtTime(r[s.key])}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="text-[11px] text-zinc-400">Con Brevo collegato (Super Admin → Integrazioni) consegne, aperture, click, bounce e disiscrizioni sono eventi reali tracciati via webhook.</p>
        </div>
      </div>
    </div>
  );
}
